import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LoadingCard } from "../components/LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  User,
  Edit,
  Trash2,
} from "lucide-react";
import PhoneInput from "../components/ui/PhoneInput";
import { formatPhone } from "../utils/phoneUtils";
import { vendorService } from "../services/vendorService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";

export default function Vendors() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [newVendor, setNewVendor] = useState({
    company_name: "",
    address: "",
    phone: "",
    email: "",
    contact: ""
  });
  const [deleteClicks, setDeleteClicks] = useState({});
  
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // API hooks
  const { data: vendorsData, isLoading: loading } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorService.getAll()
  });
  
  const createVendorMutation = useMutation({
    mutationFn: (vendorData) => vendorService.create(vendorData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast({ description: "Vendor created successfully!" });
      setNewVendor({
        company_name: "",
        address: "",
        phone: "",
        email: "",
        contact: ""
      });
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to create vendor"
      });
    }
  });

  const updateVendorMutation = useMutation({
    mutationFn: ({ id, data }) => vendorService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast({ description: "Vendor updated successfully!" });
      setIsEditDialogOpen(false);
      setEditVendor(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to update vendor"
      });
    }
  });

  const deleteVendorMutation = useMutation({
    mutationFn: (vendorId) => vendorService.delete(vendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast({ description: "Vendor deleted successfully!" });
      setDeleteClicks({});
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to delete vendor"
      });
    }
  });

  const vendors = vendorsData?.data || [];

  // Filter vendors based on search term
  const filteredVendors = vendors.filter(vendor =>
    vendor.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.contact?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddVendor = async () => {
    const vendorData = {
      ...newVendor,
      address: newVendor.address || undefined,
      phone: newVendor.phone || undefined,
      email: newVendor.email || undefined,
      contact: newVendor.contact || undefined
    };
    
    createVendorMutation.mutate(vendorData);
  };

  const handleEditVendor = async () => {
    updateVendorMutation.mutate({
      id: editVendor.id,
      data: editVendor
    });
  };

  const handleDeleteVendor = async (vendorId) => {
    // Double-click to delete
    if (deleteClicks[vendorId]) {
      deleteVendorMutation.mutate(vendorId);
    } else {
      setDeleteClicks({ ...deleteClicks, [vendorId]: true });
      setTimeout(() => {
        setDeleteClicks(prev => {
          const newClicks = { ...prev };
          delete newClicks[vendorId];
          return newClicks;
        });
      }, 3000);
    }
  };

  const openEditDialog = (vendor) => {
    setEditVendor({ ...vendor });
    setIsEditDialogOpen(true);
  };

  if (loading && !vendors.length) {
    return <LoadingCard />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Vendors</h1>
          <p className="text-muted-foreground">Manage your vendor contacts</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto shrink-0">
              <Plus className="mr-2 h-4 w-4" />
              Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Vendor</DialogTitle>
              <DialogDescription>
                Add a new vendor to your database
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={newVendor.company_name}
                  onChange={(e) => setNewVendor({...newVendor, company_name: e.target.value})}
                  placeholder="ABC Materials Inc."
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="contact">Contact Person</Label>
                <Input
                  id="contact"
                  value={newVendor.contact}
                  onChange={(e) => setNewVendor({...newVendor, contact: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newVendor.email}
                    onChange={(e) => setNewVendor({...newVendor, email: e.target.value})}
                    placeholder="contact@vendor.com"
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="phone">Phone</Label>
                  <PhoneInput
                    id="phone"
                    value={newVendor.phone}
                    onChange={(val) => setNewVendor({...newVendor, phone: val})}
                    placeholder="916.232.2332"
                  />
                </div>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={newVendor.address}
                  onChange={(e) => setNewVendor({...newVendor, address: e.target.value})}
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                disabled={createVendorMutation.isPending}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddVendor} 
                disabled={!newVendor.company_name || createVendorMutation.isPending}
              >
                {createVendorMutation.isPending ? "Adding..." : "Add Vendor"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search vendors by name, email, phone, or contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Vendors Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Vendors ({filteredVendors.length})</CardTitle>
          <CardDescription>
            A list of all your vendors with their contact information
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredVendors.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No vendors found</p>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden space-y-3">
                {filteredVendors.map((vendor) => (
                  <div key={vendor.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-base">{vendor.company_name}</p>
                        {vendor.contact && (
                          <p className="text-sm text-muted-foreground mt-0.5 flex items-center">
                            <User className="mr-1 h-3 w-3" />
                            {vendor.contact}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(vendor)} className="h-8 w-8 p-0">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteVendor(vendor.id)} className={`h-8 w-8 p-0 ${deleteClicks[vendor.id] ? "text-red-600" : ""}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 space-y-1">
                      {vendor.email && (
                        <div className="text-sm flex items-center text-muted-foreground">
                          <Mail className="mr-1.5 h-3 w-3 shrink-0" />
                          <a href={`mailto:${vendor.email}`} className="text-blue-600 hover:underline truncate">{vendor.email}</a>
                        </div>
                      )}
                      {vendor.phone && (
                        <div className="text-sm flex items-center text-muted-foreground">
                          <Phone className="mr-1.5 h-3 w-3 shrink-0" />
                          <span>{formatPhone(vendor.phone)}</span>
                        </div>
                      )}
                      {vendor.address && (
                        <div className="text-sm flex items-center text-muted-foreground">
                          <MapPin className="mr-1.5 h-3 w-3 shrink-0" />
                          <span>{vendor.address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table view */}
              <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company Name</TableHead>
                <TableHead>Contact Person</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
                {filteredVendors.map((vendor) => (
                  <TableRow key={vendor.id}>
                    <TableCell className="font-medium">
                      {vendor.company_name}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {vendor.contact && (
                          <>
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>{vendor.contact}</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {vendor.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <a href={`mailto:${vendor.email}`} className="text-blue-600 hover:underline">
                            {vendor.email}
                          </a>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {vendor.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span>{formatPhone(vendor.phone)}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {vendor.address && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{vendor.address}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(vendor)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteVendor(vendor.id)}
                          className={deleteClicks[vendor.id] ? "text-red-600" : ""}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Vendor Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Vendor</DialogTitle>
            <DialogDescription>
              Update vendor information
            </DialogDescription>
          </DialogHeader>
          
          {editVendor && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_company_name">Company Name *</Label>
                <Input
                  id="edit_company_name"
                  value={editVendor.company_name}
                  onChange={(e) => setEditVendor({...editVendor, company_name: e.target.value})}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit_contact">Contact Person</Label>
                <Input
                  id="edit_contact"
                  value={editVendor.contact || ""}
                  onChange={(e) => setEditVendor({...editVendor, contact: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit_email">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={editVendor.email || ""}
                    onChange={(e) => setEditVendor({...editVendor, email: e.target.value})}
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="edit_phone">Phone</Label>
                  <PhoneInput
                    id="edit_phone"
                    value={editVendor.phone || ""}
                    onChange={(val) => setEditVendor({...editVendor, phone: val})}
                    placeholder="916.232.2332"
                  />
                </div>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="edit_address">Address</Label>
                <Input
                  id="edit_address"
                  value={editVendor.address || ""}
                  onChange={(e) => setEditVendor({...editVendor, address: e.target.value})}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsEditDialogOpen(false)}
              disabled={updateVendorMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEditVendor} 
              disabled={!editVendor?.company_name || updateVendorMutation.isPending}
            >
              {updateVendorMutation.isPending ? "Updating..." : "Update Vendor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
